/**
 * BookReader — single BookPage rendered with one of 6 editorial layouts (A–F).
 * Navigation strictly by stable ID + sort_order (prev/next from backend).
 *
 * Layout rules per approved spec (see handoff):
 *   A · Hero image + text (atmospheric openings)
 *   B · Image left / Text right
 *   C · Text left / Image right
 *   D · Editorial article (text-first with small imagery)
 *   E · Professional recipe / technology (structured sections)
 *   F · Comparison / research / table
 */
import { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { readBookPage, toggleBookmark, toggleLike, createBookSupportRequest } from "@/lib/bookApi";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const backend = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

function resolveUrl(u) {
  if (!u) return null;
  if (u.startsWith("http")) return u;
  return `${backend}${u}`;
}

// ---------- shared atoms ----------
function EditorialImage({ img, className = "", ratio = "aspect-[16/10]", testid }) {
  if (!img) return null;
  const url = resolveUrl(img.url || (img.media_id ? `/api/media/files/${img.media_id}` : null));
  if (!url) return null;
  const fx = (img.focal_x ?? 0.5) * 100;
  const fy = (img.focal_y ?? 0.5) * 100;
  return (
    <figure className={className} data-testid={testid}>
      <div className={`${ratio} w-full overflow-hidden rounded-[2px]`}>
        <img
          src={url}
          alt={img.alt || ""}
          className="w-full h-full object-cover"
          style={{ objectPosition: `${fx}% ${fy}%` }}
        />
      </div>
      {img.caption && (
        <figcaption className="text-[12px] italic opacity-60 mt-3 leading-relaxed">
          {img.caption}
        </figcaption>
      )}
    </figure>
  );
}

function VideoBlock({ page }) {
  if (!page.video_url) return null;
  const p = (page.video_provider || "").toLowerCase();
  if (p === "youtube") {
    const idMatch = page.video_url.match(/(?:v=|be\/|embed\/)([\w-]{6,})/);
    const vid = idMatch ? idMatch[1] : "";
    return (
      <div className="aspect-video w-full my-8 rounded-[2px] overflow-hidden">
        <iframe title="video" className="w-full h-full"
          src={`https://www.youtube.com/embed/${vid}`} allowFullScreen />
      </div>
    );
  }
  if (p === "vimeo") {
    const idMatch = page.video_url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
    const vid = idMatch ? idMatch[1] : "";
    return (
      <div className="aspect-video w-full my-8 rounded-[2px] overflow-hidden">
        <iframe title="video" className="w-full h-full"
          src={`https://player.vimeo.com/video/${vid}`} allowFullScreen />
      </div>
    );
  }
  return (
    <video src={page.video_url} controls
      className="w-full my-8 rounded-[2px]" />
  );
}

function Prose({ text }) {
  if (!text) return null;
  const paragraphs = text.split(/\n\n+/).filter(Boolean);
  return (
    <div className="space-y-6" data-testid="book-reader-body">
      {paragraphs.map((p, i) => (
        <p key={i} className="text-[16.5px] lg:text-[17.5px] leading-[1.9] text-[#3b241a]/95">
          {p.split("\n").map((line, j) => (
            <span key={j}>{line}{j < p.split("\n").length - 1 && <br />}</span>
          ))}
        </p>
      ))}
    </div>
  );
}

function PullQuote({ text }) {
  if (!text) return null;
  return (
    <blockquote className="my-12 border-l-2 border-[#8a6a52] pl-6 lg:pl-8 py-2"
                data-testid="book-reader-pull-quote">
      <p className="font-serif italic text-[22px] lg:text-[26px] leading-relaxed text-[#2a1810]"
         style={{ fontFamily: "'Cormorant Garamond', 'Playfair Display', serif" }}>
        {text}
      </p>
    </blockquote>
  );
}

// ---------- Layout A · Hero + text ----------
function LayoutA({ page }) {
  return (
    <div data-testid="book-layout-A">
      {page.hero_image && (
        <EditorialImage img={page.hero_image} className="mb-12 lg:mb-16"
          ratio="aspect-[16/9] lg:aspect-[21/9]" testid="book-reader-hero" />
      )}
      <div className="max-w-[680px] mx-auto">
        <TitleBlock page={page} />
        <Prose text={page.body_md} />
        <PullQuote text={page.pull_quote} />
        {page.secondary_image && (
          <EditorialImage img={page.secondary_image} className="my-12"
            ratio="aspect-[4/3]" testid="book-reader-secondary" />
        )}
        <VideoBlock page={page} />
      </div>
    </div>
  );
}

// ---------- Layout B · Image left / Text right ----------
function LayoutB({ page }) {
  return (
    <div data-testid="book-layout-B">
      <div className="lg:grid lg:grid-cols-[45%_55%] lg:gap-16">
        <div>
          {page.hero_image
            ? <EditorialImage img={page.hero_image} ratio="aspect-[4/5]" testid="book-reader-hero" />
            : null}
        </div>
        <div className="mt-8 lg:mt-0 lg:max-w-[560px] lg:pt-6">
          <TitleBlock page={page} />
          <Prose text={page.body_md} />
          <PullQuote text={page.pull_quote} />
          {page.caption && (
            <div className="mt-6 text-[13px] italic opacity-70">{page.caption}</div>
          )}
        </div>
      </div>
      {page.secondary_image && (
        <div className="max-w-[680px] mx-auto mt-16">
          <EditorialImage img={page.secondary_image} ratio="aspect-[16/10]" />
        </div>
      )}
      <VideoBlock page={page} />
    </div>
  );
}

// ---------- Layout C · Text left / Image right ----------
function LayoutC({ page }) {
  return (
    <div data-testid="book-layout-C">
      <div className="lg:grid lg:grid-cols-[55%_45%] lg:gap-16">
        <div className="lg:max-w-[560px] order-2 lg:order-1 mt-8 lg:mt-0 lg:pt-6">
          <TitleBlock page={page} />
          <Prose text={page.body_md} />
          <PullQuote text={page.pull_quote} />
          {page.caption && (
            <div className="mt-6 text-[13px] italic opacity-70">{page.caption}</div>
          )}
        </div>
        <div className="order-1 lg:order-2">
          {page.hero_image
            ? <EditorialImage img={page.hero_image} ratio="aspect-[4/5]" testid="book-reader-hero" />
            : null}
        </div>
      </div>
      {page.secondary_image && (
        <div className="max-w-[680px] mx-auto mt-16">
          <EditorialImage img={page.secondary_image} ratio="aspect-[16/10]" />
        </div>
      )}
      <VideoBlock page={page} />
    </div>
  );
}

// ---------- Layout D · Editorial article ----------
function LayoutD({ page }) {
  return (
    <div data-testid="book-layout-D" className="max-w-[680px] mx-auto">
      <TitleBlock page={page} />
      {page.hero_image && (
        <EditorialImage img={page.hero_image} className="my-10"
          ratio="aspect-[16/10]" testid="book-reader-hero" />
      )}
      <Prose text={page.body_md} />
      <PullQuote text={page.pull_quote} />
      {page.secondary_image && (
        <EditorialImage img={page.secondary_image} className="my-12"
          ratio="aspect-[4/3]" testid="book-reader-secondary" />
      )}
      <VideoBlock page={page} />
    </div>
  );
}

// ---------- Layout E · Professional recipe ----------
function RecipeSection({ label, value, testid }) {
  if (!value) return null;
  return (
    <section className="mt-10" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-[0.42em] opacity-60 mb-3">{label}</div>
      <div className="text-[16px] lg:text-[17px] leading-[1.85] whitespace-pre-wrap text-[#3b241a]">
        {value}
      </div>
    </section>
  );
}

function LayoutE({ page }) {
  const r = page.recipe || {};
  return (
    <div data-testid="book-layout-E">
      {page.hero_image && (
        <EditorialImage img={page.hero_image} className="mb-12"
          ratio="aspect-[16/9] lg:aspect-[21/9]" testid="book-reader-hero" />
      )}
      <div className="max-w-[860px] mx-auto lg:grid lg:grid-cols-[300px_1fr] lg:gap-16">
        {/* left ingredient column */}
        <aside className="lg:sticky lg:top-24 lg:self-start">
          <TitleBlock page={page} compact />
          {(r.yield_text || r.form_text) && (
            <div className="mt-6 text-[13px] opacity-80 space-y-1">
              {r.yield_text && <div>Выход · {r.yield_text}</div>}
              {r.form_text && <div>Форма · {r.form_text}</div>}
            </div>
          )}
          {r.ingredient_groups?.length > 0 && (
            <div className="mt-10" data-testid="book-recipe-ingredients">
              <div className="text-[10px] uppercase tracking-[0.42em] opacity-60 mb-4">
                Ингредиенты
              </div>
              {r.ingredient_groups.map((g, gi) => (
                <div key={gi} className="mb-6">
                  {g.title && (
                    <div className="text-[12px] italic opacity-70 mb-2">{g.title}</div>
                  )}
                  <ul className="space-y-2">
                    {(g.items || []).map((it, ii) => (
                      <li key={ii} className="flex justify-between gap-4 text-[14px] leading-relaxed border-b border-[#3b241a]/10 pb-1.5">
                        <span>{it.name}{it.note ? <em className="opacity-70"> · {it.note}</em> : null}</span>
                        <span className="opacity-90 whitespace-nowrap">
                          {[it.amount, it.unit].filter(Boolean).join(" ")}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          )}
        </aside>
        {/* right technology column */}
        <div className="mt-14 lg:mt-0">
          {r.intro && (
            <p className="text-[16.5px] leading-[1.9] italic opacity-90 mb-6">{r.intro}</p>
          )}
          <RecipeSection label="Подготовка" value={r.preparation} testid="book-recipe-preparation" />
          <RecipeSection label="Технология" value={r.technology} testid="book-recipe-technology" />
          <RecipeSection label="Температуры" value={r.temperatures} testid="book-recipe-temperatures" />
          <RecipeSection label="Выпекание" value={r.baking} testid="book-recipe-baking" />
          <RecipeSection label="Охлаждение" value={r.cooling} testid="book-recipe-cooling" />
          <RecipeSection label="Сборка" value={r.assembly} testid="book-recipe-assembly" />
          <RecipeSection label="Пропитка" value={r.soaking} testid="book-recipe-soaking" />
          <RecipeSection label="Хранение" value={r.storage} testid="book-recipe-storage" />
          <RecipeSection label="Заморозка · разморозка" value={r.freezing} testid="book-recipe-freezing" />
          <RecipeSection label="Замены" value={r.substitutions} testid="book-recipe-substitutions" />
          <RecipeSection label="Частые ошибки" value={r.common_mistakes} testid="book-recipe-mistakes" />
          <RecipeSection label="Лаборатория рекомендует" value={r.lab_recommends} testid="book-recipe-lab" />
          <RecipeSection label="Совет технолога" value={r.technologist_advice} testid="book-recipe-advice" />
          <RecipeSection label="Что важно запомнить" value={r.remember} testid="book-recipe-remember" />
          <VideoBlock page={page} />
          {page.secondary_image && (
            <EditorialImage img={page.secondary_image} className="mt-12"
              ratio="aspect-[16/10]" />
          )}
        </div>
      </div>
    </div>
  );
}

// ---------- Layout F · Comparison / Table ----------
function LayoutF({ page }) {
  const t = page.table || {};
  return (
    <div data-testid="book-layout-F" className="max-w-[860px] mx-auto">
      <TitleBlock page={page} />
      {t.intro && (
        <div className="mt-8">
          <Prose text={t.intro} />
        </div>
      )}
      {page.hero_image && (
        <EditorialImage img={page.hero_image} className="my-12"
          ratio="aspect-[16/9]" testid="book-reader-hero" />
      )}
      {t.headers?.length > 0 && (
        <div className="mt-10">
          {/* desktop table */}
          <div className="hidden md:block overflow-x-auto -mx-2" data-testid="book-layout-F-table">
            <table className="w-full text-[15px] border-collapse">
              <thead>
                <tr>
                  {t.headers.map((h, i) => (
                    <th key={i} className="text-left font-serif italic text-[17px] py-3 px-3 border-b-2 border-[#3b241a]/40">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(t.rows || []).map((row, ri) => (
                  <tr key={ri}>
                    {row.map((cell, ci) => (
                      <td key={ci} className="py-3 px-3 border-b border-[#3b241a]/12 align-top">
                        {cell}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {/* mobile stacked cards */}
          <div className="md:hidden space-y-4" data-testid="book-layout-F-cards">
            {(t.rows || []).map((row, ri) => (
              <div key={ri} className="border border-[#3b241a]/20 rounded-[2px] p-4">
                {row.map((cell, ci) => (
                  <div key={ci} className="py-1.5 border-b last:border-b-0 border-[#3b241a]/10">
                    <div className="text-[10px] uppercase tracking-[0.3em] opacity-60">
                      {t.headers[ci]}
                    </div>
                    <div className="text-[15px] mt-1">{cell}</div>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}
      {t.outro && (
        <div className="mt-12">
          <Prose text={t.outro} />
        </div>
      )}
      {t.conclusion && (
        <div className="mt-12 p-6 lg:p-8 bg-[#3b241a]/5 border-l-2 border-[#8a6a52]"
             data-testid="book-layout-F-conclusion">
          <div className="text-[10px] uppercase tracking-[0.42em] opacity-70 mb-3">
            Что запомнить
          </div>
          <div className="text-[16px] leading-[1.9] whitespace-pre-wrap">{t.conclusion}</div>
        </div>
      )}
      <VideoBlock page={page} />
    </div>
  );
}

// ---------- Title block ----------
function TitleBlock({ page, compact = false }) {
  return (
    <header className={compact ? "" : "mb-8 lg:mb-12"}>
      <h1
        className={`font-serif leading-[1.05] text-[#2a1810]`}
        style={{
          fontFamily: "'Cormorant Garamond', 'Playfair Display', serif",
          fontSize: compact ? "clamp(28px, 3vw, 40px)" : "clamp(32px, 4vw, 56px)",
        }}
        data-testid="book-reader-title">
        {page.title}
      </h1>
      {page.subtitle && (
        <div className="italic text-[17px] lg:text-[19px] mt-3 opacity-80"
             data-testid="book-reader-subtitle">
          {page.subtitle}
        </div>
      )}
    </header>
  );
}

// ---------- Action bar (like / save / question) ----------
function ActionBar({ data, onChange }) {
  const { page, volume, is_liked, is_saved } = data;
  const [askOpen, setAskOpen] = useState(false);
  const [subject, setSubject] = useState(`Вопрос по странице «${page.title}»`);
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  const doLike = async () => {
    try {
      const res = await toggleLike(volume.id, page.id);
      onChange({ ...data, is_liked: res.liked });
      toast.success(res.liked ? "Отмечено «нравится»" : "Убрано из «нравится»");
    } catch { toast.error("Не удалось изменить"); }
  };
  const doSave = async () => {
    try {
      const res = await toggleBookmark(volume.id, page.id);
      onChange({ ...data, is_saved: res.saved });
      toast.success(res.saved ? "Добавлено в закладки" : "Убрано из закладок");
    } catch { toast.error("Не удалось изменить"); }
  };

  const sendAsk = async () => {
    if (!message.trim()) return;
    setSending(true);
    try {
      await createBookSupportRequest({
        volume_id: volume.id, page_id: page.id,
        subject: subject.trim() || `Вопрос по странице «${page.title}»`,
        message: message.trim(),
        context_title: page.title,
      });
      toast.success("Вопрос отправлен. Мы ответим Вам в ближайшее время.");
      setAskOpen(false); setMessage("");
    } catch {
      toast.error("Не удалось отправить вопрос");
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <div className="max-w-[680px] mx-auto mt-16 flex flex-wrap gap-3 items-center justify-center"
           data-testid="book-reader-actions">
        <button onClick={doLike}
          className={`px-5 py-2.5 rounded-full border text-[13px] tracking-wide flex items-center gap-2 ${
            is_liked ? "bg-[#2a1810] text-[#efe4d0] border-[#2a1810]" : "border-[#3b241a]/30 text-[#3b241a]"
          }`}
          data-testid="book-reader-like">
          <span>{is_liked ? "♥" : "♡"}</span>
          <span>{is_liked ? "Понравилось" : "Нравится"}</span>
        </button>
        <button onClick={doSave}
          className={`px-5 py-2.5 rounded-full border text-[13px] tracking-wide ${
            is_saved ? "bg-[#2a1810] text-[#efe4d0] border-[#2a1810]" : "border-[#3b241a]/30 text-[#3b241a]"
          }`}
          data-testid="book-reader-save">
          {is_saved ? "В закладках" : "Сохранить"}
        </button>
        <button onClick={() => setAskOpen(true)}
          className="px-5 py-2.5 rounded-full border border-[#3b241a]/30 text-[13px] tracking-wide text-[#3b241a]"
          data-testid="book-reader-ask">
          Задать вопрос
        </button>
      </div>

      {askOpen && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-end lg:items-center justify-center px-4 py-6"
             data-testid="book-ask-modal"
             onClick={(e) => { if (e.target === e.currentTarget) setAskOpen(false); }}>
          <div className="w-full max-w-[520px] bg-[#efe6d5] text-[#3b241a] rounded-[6px] p-6 lg:p-8">
            <div className="text-[11px] uppercase tracking-[0.42em] opacity-70 mb-3">Вопрос автору</div>
            <div className="font-serif text-2xl mb-5"
                 style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              {page.title}
            </div>
            <label className="block text-[12px] opacity-70 mb-1">Тема</label>
            <input value={subject} onChange={(e) => setSubject(e.target.value)}
              className="w-full bg-transparent border-b border-[#3b241a]/40 py-2 outline-none"
              data-testid="book-ask-subject" />
            <label className="block text-[12px] opacity-70 mt-5 mb-1">Ваш вопрос</label>
            <textarea value={message} onChange={(e) => setMessage(e.target.value)}
              rows={5} className="w-full bg-white/40 border border-[#3b241a]/25 rounded-[3px] p-3 outline-none resize-none"
              data-testid="book-ask-message" />
            <div className="mt-6 flex gap-3 justify-end">
              <button onClick={() => setAskOpen(false)}
                className="px-5 py-2.5 text-[13px] opacity-70"
                data-testid="book-ask-cancel">Отменить</button>
              <button onClick={sendAsk} disabled={sending || !message.trim()}
                className="px-6 py-2.5 rounded-full bg-[#2a1810] text-[#efe4d0] text-[13px] tracking-wide disabled:opacity-50"
                data-testid="book-ask-submit">
                {sending ? "Отправка…" : "Отправить"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ---------- Navigation ----------
function ReaderNav({ data, slug }) {
  const nav = useNavigate();
  const { prev, next } = data;
  return (
    <div className="max-w-[680px] mx-auto mt-16 pt-10 border-t border-[#3b241a]/20 flex items-stretch gap-3 flex-wrap justify-between text-[13px]"
         data-testid="book-reader-nav">
      <button onClick={() => prev && nav(`/book/${slug}/pages/${prev.id}`)}
        disabled={!prev}
        className="text-left flex-1 min-w-[42%] py-3 disabled:opacity-30"
        data-testid="book-reader-prev">
        <div className="text-[10px] uppercase tracking-[0.32em] opacity-60">← Предыдущая</div>
        {prev && <div className="mt-1 font-serif text-[17px]">{prev.title}</div>}
      </button>
      <button onClick={() => nav(`/book/${slug}`)}
        className="py-3 px-2 text-[11px] uppercase tracking-[0.32em] opacity-70"
        data-testid="book-reader-toc">
        К содержанию раздела
      </button>
      <button onClick={() => next && nav(`/book/${slug}/pages/${next.id}`)}
        disabled={!next}
        className="text-right flex-1 min-w-[42%] py-3 disabled:opacity-30"
        data-testid="book-reader-next">
        <div className="text-[10px] uppercase tracking-[0.32em] opacity-60">Следующая →</div>
        {next && <div className="mt-1 font-serif text-[17px]">{next.title}</div>}
      </button>
    </div>
  );
}

// ---------- BookReader page ----------
export default function BookReader() {
  const { slug = "almond", pageId } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  const load = useCallback(async () => {
    setData(null); setErr(null);
    try {
      const d = await readBookPage(slug, pageId);
      setData(d);
      window.scrollTo({ top: 0, behavior: "instant" });
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (typeof detail === "object" && detail?.code === "no_access") {
        setErr("Доступ к Тому не оформлен.");
      } else if (e?.response?.status === 404) {
        setErr("Страница не найдена.");
      } else {
        setErr("Не удалось загрузить страницу.");
      }
    }
  }, [slug, pageId]);

  useEffect(() => { load(); }, [load]);

  if (err) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#efe6d5] text-[#3b241a] px-6">
        <div className="text-center max-w-md">
          <div className="font-serif text-2xl mb-3">{err}</div>
          <button
            className="mt-6 px-6 py-3 rounded-full bg-[#2a1810] text-[#efe4d0] text-[12px] tracking-[0.24em] uppercase"
            onClick={() => nav(`/book/${slug}/entry`)}
            data-testid="book-reader-back-entry">К обложке</button>
        </div>
      </div>
    );
  }

  if (!data) return <div className="min-h-screen bg-[#efe6d5]" data-testid="book-reader-loading" />;

  const { page } = data;

  return (
    <div className="min-h-screen bg-[#efe6d5] text-[#3b241a]" data-testid="book-reader-screen">
      {/* Top bar */}
      <div className="max-w-[860px] mx-auto px-6 lg:px-10 pt-8 pb-4 flex items-center justify-between">
        <button
          onClick={() => nav(`/book/${slug}`)}
          className="text-[11px] tracking-[0.42em] uppercase opacity-70 hover:opacity-100"
          data-testid="book-reader-back-toc">
          ← Содержание
        </button>
        <div className="text-[10px] uppercase tracking-[0.32em] opacity-60">
          {data.section?.title}{data.subsection ? ` · ${data.subsection.title}` : ""}
        </div>
      </div>

      <article className="px-6 lg:px-10 pb-16 max-w-[1160px] mx-auto">
        {page.layout === "A" && <LayoutA page={page} />}
        {page.layout === "B" && <LayoutB page={page} />}
        {page.layout === "C" && <LayoutC page={page} />}
        {page.layout === "D" && <LayoutD page={page} />}
        {page.layout === "E" && <LayoutE page={page} />}
        {page.layout === "F" && <LayoutF page={page} />}

        {user && <ActionBar data={data} onChange={setData} />}
        <ReaderNav data={data} slug={slug} />
      </article>
    </div>
  );
}
